import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityInterface,
  ActivityMediaInterface,
  FileTypeEnum,
} from '@sport-activities/sdk';
import { forkJoin, of } from 'rxjs';

import { MimeTypeEnum } from '~/component/enums/mime-type.enum';
import {
  Upload,
  UploadCollectionChanges,
} from '~/component/upload-list/upload-list.component';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { FileExtensionValidator } from '~/core/validators/file-extension/file-extension.validator';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { FileExtensionEnum as FileExtension } from '~/organizer/enums/file-extention.enum';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

@Component({
  selector: 'app-organizer-activity-creation-uploads',
  templateUrl: './uploads.component.html',
})
export class OrganizerActivityCreationPageUploadsComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  @ViewChild('input') public inputElementRef: ElementRef;

  protected readonly pageTitleKey =
    'seo.organizer.activity_creation.birthday.upload.title';

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly mimeType = MimeTypeEnum;
  private attachments: ActivityMediaInterface[];
  public toggleNameEnum = ToggleNameEnum;
  public uploadCollection: Upload[] = [];

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  // we don't update activity for uploads ... but this method is mandatory 🤮
  protected buildDataBeforeSubmit(): Partial<ActivityInterface> {
    return {};
  }

  protected buildValidationConfig(): void {
    const allowedExtensions = [FileExtension.PDF];

    this.validationConfig = {
      uploadChanges: [
        null,
        [Validators.required, FileExtensionValidator(allowedExtensions)],
      ],
    };
  }

  protected onInitHook(): void {
    this.attachments = this.activityService.activity.bookingAttachments;

    this.attachments?.forEach((attachment) => {
      const { name, size, identifier } = attachment;
      this.uploadCollection.push({
        name,
        size,
        identifier,
      });
    });
  }

  public getErrors(): ValidationErrors | null | false {
    const formControl = this.formGroup.get('uploadChanges');
    return formControl?.touched && formControl?.errors;
  }

  public saveUploads(): void {
    if (!this.formGroup.get('uploadChanges').value) {
      this.redirectAfterSubmit();

      return;
    }

    const { added, removed } = this.formGroup.get('uploadChanges')
      .value as UploadCollectionChanges;

    if (added.length === 0 && removed.length === 0) {
      this.redirectAfterSubmit();

      return;
    }

    forkJoin(
      added.map((upload) => {
        const file = upload.file;

        if (file.size === 0) {
          return of(undefined);
        }

        return this.activityService.addMedia(
          this.activityService.activity.sku,
          file,
          file.name,
          ['BOOKING_MAIL_ATTACHMENT' as FileTypeEnum]
        );
      })
    ).subscribe(() => {
      this.redirectAfterSubmit();
    });
  }

  public submit(): void {
    if (this.formGroup.invalid && this.formGroup.touched) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.saveUploads();
  }
}
